import { useRef, useState } from "react";

interface Props {
	url: string;
	accent: string;
}

export function CompactAudioPlayer({ url, accent }: Props) {
	const audioRef = useRef<HTMLAudioElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [duration, setDuration] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);

	function handleLoadedMetadata() {
		const audio = audioRef.current;
		if (!audio) return;
		if (!isFinite(audio.duration)) {
			audio.currentTime = Number.MAX_SAFE_INTEGER;
		} else {
			setDuration(audio.duration);
		}
	}

	function handleTimeUpdate() {
		const audio = audioRef.current;
		if (!audio) return;
		if (!isFinite(duration) && isFinite(audio.duration)) {
			setDuration(audio.duration);
			audio.currentTime = 0;
			return;
		}
		setCurrentTime(audio.currentTime);
	}

	function toggle() {
		const audio = audioRef.current;
		if (!audio) return;
		if (isPlaying) {
			audio.pause();
		} else {
			audio.play();
		}
	}

	const timeLeft = isPlaying ? Math.max(duration - currentTime, 0) : duration;
	const knownDuration = isFinite(timeLeft) && !isNaN(timeLeft);
	const m = knownDuration ? Math.floor(timeLeft / 60) : 0;
	const s = knownDuration ? Math.floor(timeLeft % 60) : 0;
	const timeLabel = knownDuration
		? `${m}:${s.toString().padStart(2, "0")}`
		: "--:--";
	const displayLabel = isPlaying && knownDuration ? `-${timeLabel}` : timeLabel;

	return (
		<>
			<audio
				ref={audioRef}
				src={url}
				onLoadedMetadata={handleLoadedMetadata}
				onTimeUpdate={handleTimeUpdate}
				onPlay={() => setIsPlaying(true)}
				onPause={() => setIsPlaying(false)}
				onEnded={() => {
					setIsPlaying(false);
					setCurrentTime(0);
				}}
			>
				<track kind="captions" />
			</audio>
			<button
				type="button"
				onClick={toggle}
				className="self-start inline-flex items-center gap-2 px-4 py-2 rounded-full text-white transition-opacity hover:opacity-85"
				style={{ background: accent }}
				aria-label={isPlaying ? "Pause recording" : "Play recording"}
			>
				<svg
					width="12"
					height="12"
					viewBox="0 0 12 12"
					fill="currentColor"
					aria-hidden="true"
				>
					{isPlaying ? (
						<>
							<rect x="1.5" y="1" width="3.5" height="10" rx="1" />
							<rect x="7" y="1" width="3.5" height="10" rx="1" />
						</>
					) : (
						<path d="M2 1.5L10 6L2 10.5V1.5Z" />
					)}
				</svg>
				<span className="font-ui text-sm tabular-nums">{displayLabel}</span>
			</button>
		</>
	);
}
